import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { payrolls, type PayrollItem } from '@/api/payrolls';
import { timesheets } from '@/api/timesheets';
import { PSP_CATEGORY_LABELS, type PspCategory } from '@/api/hr';
import { orgs } from '@/api/catalogs';
import { refs, type Employee, type PayrollAccountsPreviewBatchRow } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import ProjectSelect from '@/components/ProjectSelect';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useFormValidation } from '@/hooks/useFormValidation';
import DocumentToolbar from '@/shared/components/DocumentToolbar';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  period_year: number;
  period_month: number;
  payroll_kind: 'MAIN' | 'ADVANCE' | 'VACATION' | 'SICK' | 'CIVIL';
  advance_percent: string;
  expense_account_code: string;
  payroll_account_code: string;
  pdfo_account_code: string;
  wz_account_code: string;
  esv_account_code: string;
  pdfo_rate: string;
  wz_rate: string;
  esv_rate: string;
  comment: string;
  project_id: string | null;
  items: PayrollItem[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  period_year: new Date().getFullYear(),
  period_month: new Date().getMonth() + 1,
  payroll_kind: 'MAIN',
  advance_percent: '50',
  expense_account_code: '92',
  payroll_account_code: '661',
  pdfo_account_code: '6411',
  wz_account_code: '6415',
  esv_account_code: '651',
  pdfo_rate: '18.00',
  wz_rate: '1.50',
  esv_rate: '22.00',
  comment: '',
  project_id: null,
  items: [],
};

const newRow = (n: number): PayrollItem => ({
  line_number: n,
  employee_id: '',
  salary_amount: '0',
  bonus_amount: '0',
  manual_amount: '0',
  advance_amount: '0',
  norm_days: '0',
  working_days: '0',
  timesheet_id: null,
  gross_amount: '0',
  pdfo_amount: '0',
  wz_amount: '0',
  esv_amount: '0',
  net_amount: '0',
  is_one_time_aid: false,
});

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Прибрати хвостові нулі з Decimal-рядка ('9000.000' → '9000', '8.50' → '8.5').
// Для відображення у числових input'ах (оклад/дні/години приходять з БД як
// numeric з дробовою частиною, браузер показує '9000.000').
function trimNum(v: string | null | undefined): string {
  if (v == null || v === '') return '';
  const s = String(v);
  if (!s.includes('.')) return s;
  return s.replace(/\.?0+$/, '');
}

// Підказка ЄСВ з ІНДИВІДУАЛЬНИМ розміром (Закон #13 — не вигадуємо, беремо з
// порахованих per-item даних). Показує реальну ставку рядка (8.41% інвалід / 22%),
// факт мінбази (база ЄСВ > брутто → доначислено до мінзарплати) та кап 15×МЗП.
// До «Розрахувати» (немає esv_base) — загальна ставка з шапки.
function esvTip(it: PayrollItem, headerRate: string): string {
  // Реальна ставка ЄСВ — IНДИВIДУАЛЬНА per-рядок (esv_rate_individual з розрахунку:
  // 8.41% інвалід / 22% звичайна). Шапка headerRate — лише fallback ДО «Розрахувати»
  // (коли індив. ставки ще нема). РАНIШЕ бралась шапка для ВСIХ рядків → підказка
  // показувала ставку інваліда всім, хоча у БД ставки правильні per-рядок (баг).
  const esv = it.esv_amount;
  const base = it.esv_base_amount;
  const gross = it.gross_amount;
  // Індив. ставка рядка (якщо порахована) має пріоритет над шапкою.
  const indivRate = it.esv_rate_individual && Number(it.esv_rate_individual) > 0
    ? String(it.esv_rate_individual) : headerRate;
  const isDisab = Number(indivRate) < 20;
  const rateTxt = isDisab
    ? `пільгова ${trimNum(indivRate)}% (працівник з інвалідністю)`
    : `${trimNum(indivRate)}%`;
  if (!esv || Number(esv) <= 0) {
    return `ЄСВ роботодавця — ставка ${rateTxt}. Натисніть «Розрахувати» для суми.`;
  }
  const parts = [`ЄСВ роботодавця ${fmt(Number(esv))} — ставка ${rateTxt}.`];
  // Якщо сума менша за ставку×брутто — це доначислення (зарахування авансу / ліміт).
  const expected = Number(gross || 0) * Number(indivRate) / 100;
  if (Number(esv) + 0.5 < expected) {
    parts.push('Сума — донарахування за місяць (враховано вже нарахований аванс та/або '
      + 'місячний ліміт бази 15×МЗП).');
  }
  if (base && Number(base) > Number(gross || 0) + 0.01) {
    // Мінбаза рахується на МIСЯЧНУ суму всіх нарахувань (ЗП+лікарняні+відпускні),
    // не на один документ. Якщо в місяці вже були інші виплати (лікарняний/аванс),
    // вони вже «зайняли» частину мінбази → тут база = (МЗП − інші нарахування місяця),
    // тому показане число може бути менше МЗП. Це коректно (інакше мінбаза задвоїлась би).
    parts.push('Базу донараховано до мінзарплати (мінбаза ЄСВ рахується на місяць — '
      + 'якщо були інші нарахування, як лікарняні чи аванс, тут лишається частина мінбази).');
  }
  if (it.esv_base_capped) {
    parts.push('Базу обмежено максимумом 15×МЗП.');
  }
  return parts.join(' ');
}

function calcLine(
  salary: number, bonus: number, manual: number, advance: number,
  norm: number, worked: number,
  pdfoRate: number, wzRate: number, esvRate: number,
  calcTypeCode?: string | null, workedHours?: number,
) {
  // Погодинна (HOURLY): база = ставка_за_годину × відпрацьовані_години + надбавки
  // (не «ставка + надбавки» — баг, коли norm=0 давало salaryPart=ставка). Решта
  // (оклад): salary × відпрацьовано/норма (пропорція), або повний оклад якщо норми нема.
  // bonus = надбавки з наказу (read-only «Доп»); manual = ручний ввід («Додати»).
  const salaryPart = calcTypeCode === 'HOURLY'
    ? salary * (workedHours ?? 0)
    : (norm > 0 && worked >= 0 ? salary * (worked / norm) : salary);
  const gross = Math.round((salaryPart + bonus + manual) * 100) / 100;
  const pdfo = Math.round(gross * pdfoRate) / 100;
  const wz = Math.round(gross * wzRate) / 100;
  const esv = Math.round(gross * esvRate) / 100;
  const net = gross - pdfo - wz - advance;
  return {
    gross: gross.toFixed(2),
    pdfo: pdfo.toFixed(2),
    wz: wz.toFixed(2),
    esv: esv.toFixed(2),
    net: net.toFixed(2),
  };
}

const MONTHS = ['Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];

export default function PayrollEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';
  // Вид нарахування (MAIN/ADVANCE) з query-param — поле «Вид відомості» прибрано з UI.
  const [searchParams] = useSearchParams();
  const queryKind: 'MAIN' | 'ADVANCE' | 'CIVIL' =
    searchParams.get('kind') === 'ADVANCE' ? 'ADVANCE'
      : searchParams.get('kind') === 'CIVIL' ? 'CIVIL' : 'MAIN';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по payroll.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('payroll.manage');
  const docQuery = useQuery({
    queryKey: ['payroll', id],
    queryFn: () => payrolls.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const numberPreview = useNumberPreview('doc.payrolls', form.organization_id, isNew);
  const v = useFormValidation();

  function onSave() {
    if (!v.check([
      ['organization_id', form.organization_id, 'Оберіть організацію'],
      ['document_date', form.document_date, 'Вкажіть дату'],
    ])) return;
    isNew ? create.mutate() : update.mutate();
  }
  function onSaveAndPost() {
    if (!v.check([
      ['organization_id', form.organization_id, 'Оберіть організацію'],
      ['document_date', form.document_date, 'Вкажіть дату'],
    ])) return;
    createAndPost.mutate();
  }
  function onUpdateAndPost() {
    if (!v.check([
      ['organization_id', form.organization_id, 'Оберіть організацію'],
      ['document_date', form.document_date, 'Вкажіть дату'],
    ])) return;
    updateAndPost.mutate();
  }
  const [error, setError] = useState<string | null>(null);

  const empQ = useQuery({
    // active_only: не тягнути звільнених у нарахування (баг #3).
    queryKey: ['employees', form.organization_id, 'active'],
    queryFn: () => refs.employees(form.organization_id, { active_only: true }),
    enabled: !!form.organization_id,
  });

  const [tsId, setTsId] = useState<string>('');
  const tsListQ = useQuery({
    queryKey: ['timesheets-for-payroll', form.organization_id, form.period_year, form.period_month],
    queryFn: () => timesheets.list({
      organization_id: form.organization_id,
      period_year: form.period_year,
      period_month: form.period_month,
      posted: true,
      limit: 50,
    }),
    enabled: !!form.organization_id,
  });
  const tsDocQ = useQuery({
    queryKey: ['timesheet-doc', tsId],
    queryFn: () => timesheets.get(tsId),
    enabled: !!tsId,
  });

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f, organization_id: firstOrg, payroll_kind: queryKind,
        items: f.items.length ? f.items : [newRow(1)],
      });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        period_year: d.period_year,
        period_month: d.period_month,
        payroll_kind: (d.payroll_kind as 'MAIN' | 'ADVANCE' | 'VACATION' | 'SICK' | 'CIVIL') ?? 'MAIN',
        advance_percent: d.advance_percent != null ? String(d.advance_percent) : '50',
        expense_account_code: d.expense_account_code,
        payroll_account_code: d.payroll_account_code,
        pdfo_account_code: d.pdfo_account_code,
        wz_account_code: d.wz_account_code,
        esv_account_code: d.esv_account_code,
        pdfo_rate: String(d.pdfo_rate),
        wz_rate: String(d.wz_rate),
        esv_rate: String(d.esv_rate),
        comment: d.comment ?? '',
        project_id: d.project_id ?? null,
        items: d.items.map((it) => ({
          line_number: it.line_number,
          employee_id: it.employee_id,
          salary_amount: it.salary_amount ? String(it.salary_amount) : '0',
          bonus_amount: it.bonus_amount ? String(it.bonus_amount) : '0',
          manual_amount: it.manual_amount ? String(it.manual_amount) : '0',
          advance_amount: it.advance_amount ? String(it.advance_amount) : '0',
          norm_days: it.norm_days ? String(it.norm_days) : '0',
          working_days: it.working_days ? String(it.working_days) : '0',
          norm_hours: it.norm_hours ?? null,
          worked_hours: it.worked_hours ?? null,
          calc_type_code: it.calc_type_code ?? null,
          timesheet_id: it.timesheet_id ?? null,
          gross_amount: String(it.gross_amount),
          pdfo_amount: String(it.pdfo_amount),
          wz_amount: String(it.wz_amount),
          esv_amount: String(it.esv_amount),
          esv_rate_individual: it.esv_rate_individual != null ? String(it.esv_rate_individual) : undefined,
          esv_base_amount: it.esv_base_amount != null ? String(it.esv_base_amount) : undefined,
          esv_base_capped: it.esv_base_capped ?? false,
          net_amount: String(it.net_amount),
          cost_item_id: it.cost_item_id,
          comment: it.comment,
          is_one_time_aid: it.is_one_time_aid ?? false,
        })),
      });
    }
  }, [isNew, docQuery.data, availableOrgs]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);
  // Аванс: дні/години НЕ використовуються (база = планова місячна ЗП × %), тож
  // колонки Норма/Відпр. — тільки читання (як Оклад/ДОД). Редагований лише «Додати».
  const isAdvance = form.payroll_kind === 'ADVANCE';
  // Відомість ЦПХ: брутто з договору (розмір × дні/години табеля або разово);
  // сервер тягне ЛИШЕ працівників за договором ЦПХ.
  const isCivil = form.payroll_kind === 'CIVIL';
  // Працівники, доступні у ЦІЙ відомості: ЦПУ-відомість — лише виконавці за
  // договором ЦПУ; ЗП-відомості — без них (окремий вид, сервер їх однаково відсіє).
  const empOptions = useMemo<Employee[]>(() => (empQ.data ?? []).filter((e) =>
    isCivil ? e.employment_type === 'CIVIL_LAW' : e.employment_type !== 'CIVIL_LAW'),
    [empQ.data, isCivil]);

  // Закладка «Рахунки обліку»: ефективні рахунки per-employee (прод-резолвер,
  // header = коди з шапки ЦЬОГО документа) + редаговані header-поля.
  const [docTab, setDocTab] = useState<'calc' | 'accounts'>('calc');
  const accountsPreviewKind =
    form.payroll_kind === 'VACATION' ? 'VACATION'
      : form.payroll_kind === 'SICK' ? 'SICK'
      : form.payroll_kind === 'ADVANCE' ? 'ADVANCE' : 'SALARY';
  const accEmpIds = form.items.filter((it) => it.employee_id).map((it) => it.employee_id);
  const accountsQ = useQuery({
    queryKey: ['payroll-accounts-preview', form.organization_id, accountsPreviewKind,
      accEmpIds.join(','), form.expense_account_code, form.payroll_account_code,
      form.pdfo_account_code, form.wz_account_code, form.esv_account_code],
    queryFn: () => refs.payrollAccountRules.previewBatch({
      organization_id: form.organization_id,
      employee_ids: accEmpIds,
      payment_kind: accountsPreviewKind,
      header_codes: {
        expense: form.expense_account_code, payroll: form.payroll_account_code,
        pdfo: form.pdfo_account_code, wz: form.wz_account_code,
        esv: form.esv_account_code,
      },
    }),
    enabled: docTab === 'accounts' && !!form.organization_id && accEmpIds.length > 0,
  });
  // Обгортка: відпускні/лікарняні — замість Оклад показуємо Середню, замість
  // Норма/Відпр. — Дні. Розрахунок зове готовий vacation/sick (по наказу/запису).
  const isVacSick = form.payroll_kind === 'VACATION' || form.payroll_kind === 'SICK';

  // ТЧ «заповнена чим-небудь» — є рядок з обраним працівником або ненульовою
  // сумою (стартовий порожній рядок нового документа не блокує вибір організації).
  const tablePartFilled = form.items.some((it) =>
    it.employee_id ||
    Number(it.salary_amount || 0) > 0 ||
    Number(it.bonus_amount || 0) > 0 ||
    Number(it.gross_amount || 0) > 0);

  // Кнопка «Розрахувати»: для ОСНОВНОЇ відомості активна лише після заповнення
  // з табеля (є рядок з timesheet_id, п.6). Для АВАНСОВОЇ — завжди (аванс без табеля).
  const filledFromTimesheet = form.items.some((it) => it.timesheet_id);
  // VACATION/SICK: «Розрахувати» завжди активна (рахує по наказу/запису, не з табеля).
  const calcDisabled = form.payroll_kind === 'MAIN' && !filledFromTimesheet;
  // (CIVIL: розрахунок доступний завжди — разово не потребує табеля.)

  const totalGross = form.items.reduce((s, e) => s + Number(e.gross_amount || 0), 0);
  const totalPdfo = form.items.reduce((s, e) => s + Number(e.pdfo_amount || 0), 0);
  const totalWz = form.items.reduce((s, e) => s + Number(e.wz_amount || 0), 0);
  const totalEsv = form.items.reduce((s, e) => s + Number(e.esv_amount || 0), 0);
  const totalNet = form.items.reduce((s, e) => s + Number(e.net_amount || 0), 0);

  const create = useMutation({
    mutationFn: () => payrolls.create({
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      period_year: form.period_year,
      period_month: form.period_month,
      payroll_kind: form.payroll_kind,
      advance_percent: form.payroll_kind === 'ADVANCE' ? form.advance_percent : '0',
      expense_account_code: form.expense_account_code,
      payroll_account_code: form.payroll_account_code,
      pdfo_account_code: form.pdfo_account_code,
      wz_account_code: form.wz_account_code,
      esv_account_code: form.esv_account_code,
      pdfo_rate: form.pdfo_rate, wz_rate: form.wz_rate, esv_rate: form.esv_rate,
      comment: form.comment || null,
      project_id: form.project_id,
      items: form.items,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['payrolls'] });
      navigate(`/payrolls/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => payrolls.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      period_year: form.period_year,
      period_month: form.period_month,
      payroll_kind: form.payroll_kind,
      advance_percent: form.payroll_kind === 'ADVANCE' ? form.advance_percent : '0',
      expense_account_code: form.expense_account_code,
      pdfo_account_code: form.pdfo_account_code,
      wz_account_code: form.wz_account_code,
      esv_account_code: form.esv_account_code,
      pdfo_rate: form.pdfo_rate, wz_rate: form.wz_rate, esv_rate: form.esv_rate,
      comment: form.comment || null,
      project_id: form.project_id,
      items: form.items,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll', id] }),
    onError: (e) => setError(extractError(e)),
  });
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await payrolls.create({
        organization_id: form.organization_id,
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        period_year: form.period_year,
        period_month: form.period_month,
        payroll_kind: form.payroll_kind,
        advance_percent: form.payroll_kind === 'ADVANCE' ? form.advance_percent : '0',
        expense_account_code: form.expense_account_code,
        payroll_account_code: form.payroll_account_code,
        pdfo_account_code: form.pdfo_account_code,
        wz_account_code: form.wz_account_code,
        esv_account_code: form.esv_account_code,
        pdfo_rate: form.pdfo_rate, wz_rate: form.wz_rate, esv_rate: form.esv_rate,
        comment: form.comment || null,
        project_id: form.project_id,
        items: form.items,
      });
      await payrolls.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['payrolls'] });
      navigate(`/payrolls/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await payrolls.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        period_year: form.period_year,
        period_month: form.period_month,
        payroll_kind: form.payroll_kind,
        advance_percent: form.payroll_kind === 'ADVANCE' ? form.advance_percent : '0',
        expense_account_code: form.expense_account_code,
        pdfo_account_code: form.pdfo_account_code,
        wz_account_code: form.wz_account_code,
        esv_account_code: form.esv_account_code,
        pdfo_rate: form.pdfo_rate, wz_rate: form.wz_rate, esv_rate: form.esv_rate,
        comment: form.comment || null,
        project_id: form.project_id,
        items: form.items,
      });
      await payrolls.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll', id] });
      qc.invalidateQueries({ queryKey: ['payrolls'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => payrolls.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll', id] });
      qc.invalidateQueries({ queryKey: ['payrolls'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => payrolls.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payrolls'] });
      navigate('/payrolls');
    },
  });

  // СЕРВЕРНИЙ розрахунок (єдине джерело істини, Закон #6): дьоргає
  // calculate-preview → усі суми (gross/ПДФО/ВЗ/ЄСВ) рахує backend з усіма
  // правилами (інвалід 8.41%, мінбаза ЄСВ, погодинна оплата, ставки за датою).
  // Замінює локальний calcLine — інакше при проведенні backend дасть 422 (mismatch).
  const serverCalc = useMutation({
    mutationFn: () => {
      const empIds = form.items.filter((it) => it.employee_id).map((it) => it.employee_id);
      // «Розрахувати» = ЧИСТИЙ розрахунок БЕЗ ручної корекції (manual не шлемо).
      // Корекція — разова ручна правка ПОВЕРХ розрахунку; «Розрахувати» її скидає
      // (onSuccess обнуляє поле «Корекція»), щоб брутто = чистий розрахунок.
      return payrolls.calculatePreview({
        organization_id: form.organization_id,
        period_year: form.period_year,
        period_month: form.period_month,
        employee_ids: empIds.length > 0 ? empIds : undefined,
        payroll_kind: form.payroll_kind,
        advance_percent: form.payroll_kind === 'ADVANCE' ? form.advance_percent : '0',
      });
    },
    onSuccess: (res) => {
      if (res.items.length === 0) {
        setError('Розрахунок: не знайдено співробітників/сум за період.');
        return;
      }
      const first = res.items[0]!;
      // «Розрахувати» оновлює суми (брутто/ПДФО/ВЗ/ЄСВ/нетто) + ставки + надбавки
      // («Доп» = bonus_amount: планові нарахування/індексація з наказу — рахує сервер).
      // Норму/Відпр. (norm_days/working_days) НЕ чіпаємо — їх заповнює табель/користувач.
      setForm((f) => {
        const calcByEmp = new Map(res.items.map((r) => [r.employee_id, r]));
        const items = f.items.map((row) => {
          const r = calcByEmp.get(row.employee_id);
          if (!r) return row;
          return {
            ...row,
            // norm_days/working_days — лишаємо як у формі (табель).
            manual_amount: '0',  // «Розрахувати» скидає ручну корекцію
            salary_amount: r.salary_amount,
            bonus_amount: r.bonus_amount,   // надбавки/індексація з наказу (сервер)
            advance_amount: r.advance_amount,
            calc_type_code: r.calc_type_code,  // для коректного ручного перерахунку
            gross_amount: r.gross_amount,
            pdfo_amount: r.pdfo_amount,
            wz_amount: r.wz_amount,
            esv_amount: r.esv_amount,
            esv_rate_individual: r.esv_rate,        // індив. ставка ЄСВ (8.41%/22%)
            esv_base_amount: r.esv_base_amount,     // база (мінбаза/кап)
            esv_base_capped: r.esv_base_capped,
            net_amount: r.net_amount,
            // Обгортка VACATION/SICK: середня/дні/наказ/підказка.
            source_kind: r.source_kind,
            source_order_id: r.source_order_id,
            average_daily_wage: r.average_daily_wage,
            days_count: r.days_count,
            needs_order_hint: r.needs_order_hint,
          };
        });
        return {
          ...f, items,
          pdfo_rate: first.pdfo_rate, wz_rate: first.wz_rate, esv_rate: first.esv_rate,
        };
      });
      setError(null);
    },
    onError: (e) => setError(extractError(e)),
  });

  function setItem(i: number, patch: Partial<PayrollItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => {
        if (idx !== i) return e;
        const next = { ...e, ...patch };
        const triggers = ['salary_amount', 'bonus_amount', 'manual_amount',
                          'advance_amount', 'norm_days', 'working_days', 'gross_amount'];
        if (triggers.some((k) => k in patch)) {
          if (f.payroll_kind === 'ADVANCE') {
            // АВАНС: брутто = база×% (рахує сервер) + «Додати» руками. Фронт НЕ
            // знає місячну базу авансу, тож НЕ перераховує через MAIN-формулу
            // (інакше manual «збивав» брутто). Інкрементально: лишаємо серверне
            // брутто без manual і додаємо нове manual. Тільки manual редагований.
            const manualNew = Number(next.manual_amount || 0);
            const manualOld = Number(e.manual_amount || 0);
            const baseGross = Number(e.gross_amount || 0) - manualOld; // gross без manual
            const gross = Math.round((baseGross + manualNew) * 100) / 100;
            const pdfo = Math.round(gross * Number(f.pdfo_rate || 0)) / 100;
            const wz = Math.round(gross * Number(f.wz_rate || 0)) / 100;
            const esv = Math.round(gross * Number(f.esv_rate || 0)) / 100;
            next.gross_amount = gross.toFixed(2);
            next.pdfo_amount = pdfo.toFixed(2);
            next.wz_amount = wz.toFixed(2);
            next.esv_amount = esv.toFixed(2);
            next.net_amount = (gross - pdfo - wz).toFixed(2);
          } else {
            const salary = Number(next.salary_amount || 0);
            const bonus = Number(next.bonus_amount || 0);
            const manual = Number(next.manual_amount || 0);
            const advance = Number(next.advance_amount || 0);
            const norm = Number(next.norm_days || 0);
            const worked = Number(next.working_days || 0);
            const calc = calcLine(
              salary, bonus, manual, advance, norm, worked,
              Number(f.pdfo_rate || 0),
              Number(f.wz_rate || 0),
              Number(f.esv_rate || 0),
              next.calc_type_code, Number(next.worked_hours || 0),
            );
            next.gross_amount = calc.gross;
            next.pdfo_amount = calc.pdfo;
            next.wz_amount = calc.wz;
            next.esv_amount = calc.esv;
            next.net_amount = calc.net;
          }
        }
        return next;
      }),
    }));
  }

  function fillFromTimesheet() {
    const ts = tsDocQ.data;
    if (!ts) return;
    // Відомість ЦПХ: з табеля тягнемо ЛИШЕ працівників за договором ЦПХ
    // (галка «включно з ЦПХ» при заповненні табеля); ЗП-відомості — без ЦПХ.
    const civilIds = new Set(
      (empQ.data ?? []).filter((e) => e.employment_type === 'CIVIL_LAW').map((e) => e.id));
    const tsItems = ts.items.filter((it) =>
      isCivil ? civilIds.has(it.employee_id) : !civilIds.has(it.employee_id));
    if (tsItems.length === 0) {
      setError(isCivil
        ? 'У табелі немає працівників за ЦПУ (заповніть табель з галкою «включно з ЦПХ»).'
        : 'У табелі немає штатних працівників.');
      return;
    }
    setForm((f) => {
      // Рядки СТВОРЮЮТЬСЯ з табеля (баг #3: працювало лише після «Усі співробітники»).
      // Якщо у документі лише порожні рядки (employee_id='') — замінюємо їх рядками
      // з табеля; якщо вже є змістовні — оновлюємо дні для тих, хто є у табелі.
      const hasReal = f.items.some((it) => it.employee_id);
      const byEmp = new Map(tsItems.map((it) => [it.employee_id, it]));
      const base = hasReal
        ? f.items
        : tsItems.map((it, idx) => ({ ...newRow(idx + 1), employee_id: it.employee_id }));
      const next = base.map((row, idx) => {
        const tsRow = byEmp.get(row.employee_id);
        if (!tsRow) return row;
        // Лише прив'язка табеля + дні/години. Суми рахує сервер («Розрахувати»).
        // Норма-години = норма-дні × 8; відпрацьовані години — з табеля (якщо є).
        return {
          ...row,
          line_number: idx + 1,
          norm_days: Number(ts.norm_days).toFixed(2),
          working_days: Number(tsRow.working_days).toFixed(2),
          norm_hours: (Number(ts.norm_days) * 8).toFixed(2),
          worked_hours: tsRow.worked_hours != null ? String(tsRow.worked_hours) : null,
          timesheet_id: ts.id,
        };
      });
      return { ...f, items: next };
    });
    setError('Рядки заповнено з табеля. Натисніть «Розрахувати» для сум.');
  }

  function addRow() {
    setForm((f) => ({ ...f, items: [...f.items, newRow(f.items.length + 1)] }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }
  function fillFromEmployees() {
    if (!empQ.data || empQ.data.length === 0) return;
    const src = empOptions;
    if (src.length === 0) return;
    setForm((f) => ({
      ...f,
      items: src.map((e, idx) => ({
        line_number: idx + 1,
        employee_id: e.id,
        gross_amount: '0',
        pdfo_amount: '0',
        wz_amount: '0',
        esv_amount: '0',
        net_amount: '0',
      })),
    }));
  }

  // UX β: warning якщо новий документ а user не має payroll.manage у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: '#fef3c7', borderRadius: 6,
            border: '1px solid #fbbf24', color: '#92400e',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати нарахування зарплати у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/payrolls')}>Скасувати</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew
            ? `Нове нарахування (${form.payroll_kind === 'ADVANCE' ? 'аванс' : form.payroll_kind === 'CIVIL' ? 'по ЦПУ' : 'основна ЗП'})`
            : `Нарахування (${form.payroll_kind === 'ADVANCE' ? 'аванс' : form.payroll_kind === 'CIVIL' ? 'по ЦПУ' : 'основна ЗП'}) № ${form.document_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ проведено
            </span>
          )}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/payrolls')}
          onSave={onSave}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={isNew ? onSaveAndPost : onUpdateAndPost}
          postDisabled={
            isNew
              ? create.isPending || createAndPost.isPending
              : updateAndPost.isPending || update.isPending || form.items.length === 0
          }
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
          onPrint={() => navigate(`/payrolls/${id}/print`)}
        />
      </div>

      {v.banner()}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Організація</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew || tablePartFilled}
                  title={tablePartFilled
                    ? 'Очистіть табличну частину, щоб змінити організацію'
                    : undefined}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field">
          <label>№ документа</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>Дата</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(val) => setForm({ ...form, document_date: val })}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>Місяць</label>
          <select value={form.period_month}
                  onChange={(e) => setForm({ ...form, period_month: Number(e.target.value) })}
                  disabled={isPosted}>
            {MONTHS.map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Рік</label>
          <input type="number" value={form.period_year}
                 onChange={(e) => setForm({ ...form, period_year: Number(e.target.value) })}
                 disabled={isPosted} />
        </div>
        {/* «Вид відомості» прибрано з UI (2026-06-25): вид (Основна/Авансова)
            задається з реєстру «Створити ▾» через query-param kind, не редагується
            у формі. Показуємо лише як read-only мітку. */}
        {form.payroll_kind === 'ADVANCE' && (
          <div className="field">
            <label>% авансу</label>
            <input type="number" step="0.01" min="0" max="100"
                   value={form.advance_percent}
                   onChange={(e) => setForm({ ...form, advance_percent: e.target.value })}
                   disabled={isPosted}
                   title="Відсоток окладу для авансу. Натисніть «Розрахувати» для сум." />
          </div>
        )}
        <div className="field">
          <label>Проект / напрям</label>
          <ProjectSelect
            value={form.project_id}
            organizationId={form.organization_id}
            onChange={(v) => setForm({ ...form, project_id: v })}
            disabled={isPosted} />
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem' }}>
        <h3 style={{ margin: 0 }}>Співробітники</h3>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {/* Аванс не залежить від табеля — приховуємо вибір табеля + кнопку. */}
          {!isPosted && !isAdvance && tsListQ.data && tsListQ.data.items.length > 0 && (
            <>
              <select value={tsId} onChange={(e) => setTsId(e.target.value)}
                      style={{ minWidth: 200 }}>
                <option value="">— Табель —</option>
                {tsListQ.data.items.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.document_number} (норма {t.norm_days})
                  </option>
                ))}
              </select>
              <button onClick={fillFromTimesheet} disabled={!tsId || tsDocQ.isFetching}>
                Заповнити з табеля
              </button>
            </>
          )}
          {/* «Усі співробітники» / «Заповнити оклади» — лише для АВАНСОВОЇ
              відомості. Основна (MAIN) заповнюється ВИКЛЮЧНО з табеля (п.6). */}
          {!isPosted && (form.payroll_kind === 'ADVANCE' || isVacSick || isCivil) && empOptions.length > 0 && form.items.length <= 1 && (
            <button onClick={fillFromEmployees}>
              {isCivil ? 'Усі виконавці ЦПУ' : 'Усі співробітники'} ({empOptions.length})
            </button>
          )}
          {!isPosted && (
            <button className="primary" onClick={() => serverCalc.mutate()}
              disabled={serverCalc.isPending || calcDisabled}
              title={calcDisabled
                ? 'Спершу заповніть рядки з табеля'
                : "Точний розрахунок: інвалід 8.41%, мінбаза ЄСВ, погодинна оплата, ставки за датою. Обов'язково перед проведенням."}>
              Розрахувати
            </button>
          )}
        </div>
      </div>

      {/* Закладки: таблична частина ↔ рахунки обліку (рішення власника 2026-07-07). */}
      <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 6,
        overflow: 'hidden', width: 'fit-content', marginBottom: '0.5rem' }}>
        {([['calc', 'Розрахунок'], ['accounts', 'Рахунки обліку']] as const).map(([t, l]) => (
          <button key={t} onClick={() => setDocTab(t)}
            style={{ padding: '0.3rem 0.9rem', border: 'none', cursor: 'pointer', fontSize: '0.8rem',
              background: docTab === t ? 'var(--primary)' : 'transparent',
              color: docTab === t ? '#fff' : 'var(--text-secondary)' }}>
            {l}
          </button>
        ))}
      </div>

      {docTab === 'accounts' && (
        <div>
          <p style={{ color: 'var(--muted)', fontSize: '0.8rem', marginTop: 0 }}>
            Рахунки документа. Позначка «інд» у таблиці — індивідуальний рахунок
            (правило обліку / персональний / ЦПУ): зміна рахунків документа на
            нього не впливає.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
            {([
              ['expense_account_code', 'Витрати (Дт)'],
              ['payroll_account_code', 'Розрахунки'],
              ['pdfo_account_code', 'ПДФО'],
              ['wz_account_code', 'ВЗ'],
              ['esv_account_code', 'ЄСВ'],
            ] as const).map(([f, l]) => (
              <div className="field" key={f} style={{ width: 110, margin: 0 }}>
                <label>{l}</label>
                <input value={form[f]} disabled={isPosted}
                  onChange={(e) => setForm({ ...form, [f]: e.target.value })} />
              </div>
            ))}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                <th style={{ padding: '0.3rem 0.4rem' }}>Працівник</th>
                <th style={{ padding: '0.3rem 0.4rem' }}>Витрати (Дт)</th>
                <th style={{ padding: '0.3rem 0.4rem' }}>Розрахунки (Кт)</th>
                <th style={{ padding: '0.3rem 0.4rem' }}>ПДФО</th>
                <th style={{ padding: '0.3rem 0.4rem' }}>ВЗ</th>
                <th style={{ padding: '0.3rem 0.4rem' }}>ЄСВ</th>
              </tr>
            </thead>
            <tbody>
              {(accountsQ.data?.rows ?? []).map((r: PayrollAccountsPreviewBatchRow) => {
                const emp = empQ.data?.find((e) => e.id === r.employee_id);
                // «інд» — ЛИШЕ де рахунок НЕ з документа (правило/картка/ЦПУ).
                const cell = (it: { account_code: string | null; source: string }) => (
                  <span>
                    {it.account_code ?? '—'}
                    {['rule', 'card', 'civil_default'].includes(it.source) && (
                      <span style={{ marginLeft: 4, fontSize: '0.68rem', color: '#94a3b8' }}
                        title={it.source === 'rule' ? 'правило обліку'
                          : it.source === 'card' ? 'персональний рахунок працівника'
                          : 'ЦПУ-дефолт'}>
                        інд
                      </span>
                    )}
                  </span>
                );
                return (
                  <tr key={r.employee_id} style={{ borderTop: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '0.25rem 0.4rem' }}>{emp?.name ?? r.employee_id}</td>
                    <td style={{ padding: '0.25rem 0.4rem' }}>{cell(r.expense)}</td>
                    <td style={{ padding: '0.25rem 0.4rem' }}>{cell(r.payroll)}</td>
                    <td style={{ padding: '0.25rem 0.4rem' }}>{cell(r.pdfo)}</td>
                    <td style={{ padding: '0.25rem 0.4rem' }}>{cell(r.wz)}</td>
                    <td style={{ padding: '0.25rem 0.4rem' }}>{cell(r.esv)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {accEmpIds.length === 0 && (
            <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
              Заповніть рядки на закладці «Розрахунок» — тут з'являться ефективні
              рахунки кожного працівника.
            </p>
          )}
        </div>
      )}

      <div style={{ display: docTab === 'calc' ? 'block' : 'none' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left', fontSize: '0.68rem' }}>
            <th style={{ padding: '0.2rem 0.3rem', width: 22 }}>#</th>
            <th style={{ padding: '0.2rem 0.3rem' }}>Співробітник</th>
            <th style={{ padding: '0.2rem 0.3rem', width: 90 }}>{isVacSick ? 'Середня' : isCivil ? 'Розмір' : 'Оклад'}</th>
            <th style={{ padding: '0.2rem 0.3rem', width: 70, textAlign: 'center' }}>
              {isVacSick ? 'Дні' : 'Норма'}
              <div style={{ display: 'flex', fontSize: '0.6rem', fontWeight: 400, color: '#94a3b8' }}>
                <span style={{ flex: 1 }}>дн</span><span style={{ flex: 1 }}>год</span>
              </div>
            </th>
            <th style={{ padding: '0.2rem 0.3rem', width: 70, textAlign: 'center' }}>
              Відпр.
              <div style={{ display: 'flex', fontSize: '0.6rem', fontWeight: 400, color: '#94a3b8' }}>
                <span style={{ flex: 1 }}>дн</span><span style={{ flex: 1 }}>год</span>
              </div>
            </th>
            {!isCivil && (
            <th style={{ padding: '0.2rem 0.3rem', width: 80 }}
                title="Надбавки/індексація з наказу — рахує сервер (тільки для читання)">ДОД</th>
            )}
            <th style={{ padding: '0.2rem 0.3rem', width: 80 }}
                title="Ручна корекція брутто: плюс — доплата/премія, мінус — зменшення. Входить у брутто та податки.">Корекція</th>
            <th style={{ padding: '0.2rem 0.3rem', width: 95 }}>Брутто</th>
            <th style={{ padding: '0.2rem 0.3rem', width: 85 }}>ПДФО</th>
            <th style={{ padding: '0.2rem 0.3rem', width: 80 }}>ВЗ</th>
            <th style={{ padding: '0.2rem 0.3rem', width: 85 }}>ЄСВ</th>
            {!isCivil && <th style={{ padding: '0.2rem 0.3rem', width: 85 }}>Аванс</th>}
            <th style={{ padding: '0.2rem 0.3rem', width: 95 }}>До виплати</th>
            {!isCivil && (
            <th style={{ padding: '0.2rem 0.3rem', width: 45, textAlign: 'center' }}
                title="Одноразова матеріальна допомога — НЕ враховується у середньому заробітку для відпусток/лікарняних">
              Мат.&shy;доп.
            </th>
            )}
            <th style={{ padding: '0.2rem 0.3rem', width: 30 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.15rem 0.4rem' }}>{it.line_number}</td>
              <td style={{ padding: '0.15rem 0.4rem' }}>
                <select value={it.employee_id}
                        onChange={(e) => setItem(i, { employee_id: e.target.value })}
                        disabled={isPosted}
                        style={{ width: '100%', fontSize: '0.68rem' }}>
                  <option value="">—</option>
                  {empOptions.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.code ? `${e.code} — ` : ''}{e.name}
                    </option>
                  ))}
                </select>
              </td>
              <td style={{ padding: '0.15rem 0.4rem' }}>
                {/* Оклад (MAIN/ADVANCE) АБО Середня ЗП (VACATION/SICK) — read-only. */}
                <input type="number" step="0.01" className="no-spin pe-cell"
                       value={isVacSick
                         ? (trimNum(it.average_daily_wage) || '0')
                         : (trimNum(it.salary_amount) || '0')}
                       readOnly tabIndex={-1}
                       title={isVacSick
                         ? 'Середньоденна ЗП (розрахунок по наказу/запису, тільки читання)'
                         : 'Оклад/ставка з кадрових даних (тільки читання)'}
                       style={{ textAlign: 'right', width: '100%', background: '#f1f5f9',
                                color: '#475569' }} />
              </td>
              <td style={{ padding: '0.15rem 0.3rem' }}>
                {isVacSick ? (
                  // Дні відпустки/лікарняного (з наказу/запису, read-only).
                  <input type="number" className="no-spin pe-cell"
                         value={it.days_count ?? ''} readOnly tabIndex={-1}
                         placeholder="—"
                         title={it.needs_order_hint || 'Дні з наказу/запису'}
                         style={{ textAlign: 'center', width: '100%', background: '#f1f5f9',
                                  color: it.needs_order_hint ? '#b45309' : '#475569' }} />
                ) : (
                <div style={{ display: 'flex', gap: 2 }}>
                  <input type="number" step="0.5" min={0} max={31} className="no-spin pe-cell"
                         value={trimNum(it.norm_days) || '0'}
                         onChange={(e) => setItem(i, { norm_days: e.target.value })}
                         disabled={isPosted} readOnly={!!it.timesheet_id || isAdvance}
                         title={isAdvance ? 'Аванс не залежить від днів (тільки читання)'
                           : it.timesheet_id ? 'Дні (з табеля — тільки читання)' : 'Дні'}
                         style={{ textAlign: 'right', width: '50%', padding: '0.1rem 0.2rem',
                                  background: isAdvance ? '#f1f5f9' : it.timesheet_id ? '#ecfdf5' : undefined }} />
                  <input type="number" step="0.5" min={0} className="no-spin pe-cell"
                         value={trimNum(it.norm_hours)}
                         onChange={(e) => setItem(i, { norm_hours: e.target.value || null })}
                         disabled={isPosted} readOnly={!!it.timesheet_id || isAdvance} placeholder="—"
                         title={isAdvance ? 'Аванс не залежить від годин (тільки читання)'
                           : it.timesheet_id ? 'Години (з табеля — тільки читання)' : 'Години'}
                         style={{ textAlign: 'right', width: '50%', padding: '0.1rem 0.2rem',
                                  background: isAdvance ? '#f1f5f9' : it.timesheet_id ? '#ecfdf5' : undefined }} />
                </div>
                )}
              </td>
              <td style={{ padding: '0.15rem 0.3rem' }}>
                <div style={{ display: 'flex', gap: 2 }}>
                  <input type="number" step="0.5" min={0} max={31} className="no-spin pe-cell"
                         value={trimNum(it.working_days) || '0'}
                         onChange={(e) => setItem(i, { working_days: e.target.value })}
                         disabled={isPosted} readOnly={!!it.timesheet_id || isAdvance}
                         title={isAdvance ? 'Аванс не залежить від днів (тільки читання)'
                           : it.timesheet_id ? 'Дні (з табеля — тільки читання)' : 'Дні'}
                         style={{ textAlign: 'right', width: '50%', padding: '0.1rem 0.2rem',
                                  background: isAdvance ? '#f1f5f9' : it.timesheet_id ? '#ecfdf5' : undefined }} />
                  <input type="number" step="0.5" min={0} className="no-spin pe-cell"
                         value={trimNum(it.worked_hours)}
                         onChange={(e) => setItem(i, { worked_hours: e.target.value || null })}
                         disabled={isPosted} readOnly={!!it.timesheet_id || isAdvance} placeholder="—"
                         title={isAdvance ? 'Аванс не залежить від годин (тільки читання)'
                           : it.timesheet_id ? 'Години (з табеля — тільки читання)' : 'Години'}
                         style={{ textAlign: 'right', width: '50%', padding: '0.1rem 0.2rem',
                                  background: isAdvance ? '#f1f5f9' : it.timesheet_id ? '#ecfdf5' : undefined }} />
                </div>
              </td>
              {!isCivil && (
              <td style={{ padding: '0.15rem 0.3rem' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {/* «Доп» = надбавки/індексація з наказу (рахує сервер) — readonly. */}
                  <input type="number" step="0.01" className="no-spin pe-cell"
                         value={trimNum(it.bonus_amount) || '0'}
                         readOnly tabIndex={-1}
                         title="Надбавки/індексація з наказу — рахує сервер (тільки читання)"
                         style={{ textAlign: 'right', width: '100%', background: '#f1f5f9',
                                  color: '#475569' }} />
                  {Number(it.bonus_amount || 0) > 0 && (
                    <InfoQ title="Надбавки/індексація з наказу (планові нарахування). Рахує сервер; ручний ввід — у колонці «Корекція»." />
                  )}
                </div>
              </td>
              )}
              <td style={{ padding: '0.15rem 0.3rem' }}>
                {/* «Корекція» = ручний ввід: плюс=доплата, мінус=зменшення брутто. */}
                <input type="number" step="0.01" className="no-spin pe-cell"
                       value={trimNum(it.manual_amount) || '0'}
                       onChange={(e) => setItem(i, { manual_amount: e.target.value })}
                       disabled={isPosted}
                       title="Ручна корекція брутто: плюс — доплата/премія, мінус — зменшення"
                       style={{ textAlign: 'right', width: '100%',
                                color: Number(it.manual_amount || 0) < 0 ? '#dc2626' : undefined }} />
              </td>
              <td style={{ padding: '0.15rem 0.4rem', textAlign: 'right', fontWeight: 600, background: '#f1f5f9' }}>
                {fmt(Number(it.gross_amount))}
                {it.psp_applied && Number(it.psp_amount || 0) > 0 && (
                  <div
                    data-testid={`payroll-psp-badge-${i}`}
                    title={`Застосовано ПСП (${
                      it.psp_category ? PSP_CATEGORY_LABELS[it.psp_category as PspCategory] ?? it.psp_category : ''
                    })`}
                    style={{
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      color: '#1e3a8a',
                      background: '#dbeafe',
                      borderRadius: 3,
                      padding: '1px 4px',
                      marginTop: 2,
                      display: 'inline-block',
                    }}
                  >
                    ПСП −{fmt(Number(it.psp_amount))}
                  </div>
                )}
              </td>
              <td style={{ padding: '0.15rem 0.3rem' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {/* ПДФО/ВЗ/ЄСВ заповнює «Розрахувати» (сервер) — тільки читання. */}
                  <input type="number" step="0.01" className="no-spin pe-cell"
                         value={trimNum(it.pdfo_amount)}
                         readOnly tabIndex={-1}
                         style={{ textAlign: 'right', width: '100%', background: '#f1f5f9',
                                  color: '#475569' }} />
                  <InfoQ title={`ПДФО — ${trimNum(form.pdfo_rate)}% від брутто. Рахує «Розрахувати».`} />
                </div>
              </td>
              <td style={{ padding: '0.15rem 0.3rem' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input type="number" step="0.01" className="no-spin pe-cell"
                         value={trimNum(it.wz_amount)}
                         readOnly tabIndex={-1}
                         style={{ textAlign: 'right', width: '100%', background: '#f1f5f9',
                                  color: '#475569' }} />
                  <InfoQ title={`Військовий збір — ${trimNum(form.wz_rate)}% від брутто. Рахує «Розрахувати».`} />
                </div>
              </td>
              <td style={{ padding: '0.15rem 0.3rem' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input type="number" step="0.01" className="no-spin pe-cell"
                         value={trimNum(it.esv_amount)}
                         readOnly tabIndex={-1}
                         style={{ textAlign: 'right', width: '100%', background: '#f1f5f9',
                                  color: '#475569' }} />
                  <InfoQ title={esvTip(it, form.esv_rate)} />
                </div>
              </td>
              {!isCivil && (
              <td style={{ padding: '0.15rem 0.3rem' }}>
                <input type="number" step="0.01" className="no-spin pe-cell"
                       value={trimNum(it.advance_amount) || '0'}
                       onChange={(e) => setItem(i, { advance_amount: e.target.value })}
                       disabled={isPosted}
                       style={{ textAlign: 'right', width: '100%' }} />
              </td>
              )}
              <td style={{ padding: '0.15rem 0.3rem', textAlign: 'right', fontWeight: 600 }}>
                {fmt(Number(it.net_amount))}
              </td>
              {!isCivil && (
              <td style={{ padding: '0.15rem 0.3rem', textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={it.is_one_time_aid ?? false}
                  disabled={isPosted}
                  onChange={(e) => setItem(i, { is_one_time_aid: e.target.checked })}
                  title="Виключити цю суму з чисельника середнього заробітку для відпусток/лікарняних"
                />
              </td>
              )}
              <td style={{ padding: '0.15rem 0.3rem' }}>
                {!isPosted && (
                  <button onClick={() => removeRow(i)} className="danger"
                          style={{ padding: '0.2rem 0.45rem' }}>×</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 600, fontSize: '0.78rem' }}>
            <td colSpan={isCivil ? 6 : 7} style={{ padding: '0.25rem 0.4rem', textAlign: 'right' }}>Усього:</td>
            <td style={{ padding: '0.25rem 0.4rem', textAlign: 'right' }}>{fmt(totalGross)}</td>
            <td style={{ padding: '0.25rem 0.4rem', textAlign: 'right' }}>{fmt(totalPdfo)}</td>
            <td style={{ padding: '0.25rem 0.4rem', textAlign: 'right' }}>{fmt(totalWz)}</td>
            <td style={{ padding: '0.25rem 0.4rem', textAlign: 'right' }}>{fmt(totalEsv)}</td>
            {!isCivil && (
            <td style={{ padding: '0.25rem 0.4rem', textAlign: 'right' }}>
              {fmt(form.items.reduce((s, e) => s + Number(e.advance_amount || 0), 0))}
            </td>
            )}
            <td style={{ padding: '0.25rem 0.4rem', textAlign: 'right' }}>{fmt(totalNet)}</td>
            {!isCivil && <td></td>}
            <td></td>
          </tr>
        </tfoot>
      </table>
      {!isPosted && (
        <button onClick={addRow} style={{ marginTop: '0.75rem' }}>+ Додати рядок</button>
      )}
      </div>

      <div className="field" style={{ marginTop: '1rem' }}>
        <label>Коментар</label>
        <input value={form.comment}
               onChange={(e) => setForm({ ...form, comment: e.target.value })}
               disabled={isPosted} />
      </div>

      {error && <div className="error">{error}</div>}

      <PayrollPspBreakdown items={form.items} empMap={empQ.data ?? []} />
    </div>
  );
}

// Акуратний знак питання зі ШВИДКОЮ підказкою (CSS hover — миттєво, без затримки
// нативного title ~1с). Використовується у колонках «ДОД» та податках.
function InfoQ({ title }: { title: string }) {
  return (
    <span className="pe-tip" style={{ marginLeft: 3, verticalAlign: 'middle', flex: 'none' }}>
      <span
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 13, height: 13, borderRadius: '50%',
          background: '#e2e8f0', color: '#475569', fontSize: '0.58rem',
          fontWeight: 700, cursor: 'help',
        }}
      >?</span>
      <span className="pe-tip-bubble">{title}</span>
    </span>
  );
}

interface PspBreakdownRowEmployee { id: string; code: string | null; name: string }

interface PayrollPspBreakdownProps {
  items: PayrollItem[];
  empMap: PspBreakdownRowEmployee[];
}

// Block 9 (2026-05-27): розкриває обчислення ПДФО з урахуванням ПСП.
// Показуємо тільки рядки де psp_applied=true — інших впливу немає.
export function PayrollPspBreakdown({ items, empMap }: PayrollPspBreakdownProps) {
  const applied = items.filter((it) => it.psp_applied && Number(it.psp_amount || 0) > 0);
  if (applied.length === 0) return null;
  const nameOf = (id: string) => {
    const e = empMap.find((x) => x.id === id);
    if (!e) return '—';
    return `${e.code ? `${e.code} — ` : ''}${e.name}`;
  };
  return (
    <div
      data-testid="payroll-psp-breakdown"
      style={{
        marginTop: '1rem',
        padding: '0.75rem',
        background: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: 6,
      }}
    >
      <strong style={{ color: '#1e3a8a' }}>
        Деталізація ПДФО з урахуванням ПСП ({applied.length})
      </strong>
      <table style={{ width: '100%', marginTop: '0.5rem', fontSize: '0.82rem', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ textAlign: 'left', color: '#1e3a8a' }}>
            <th style={{ padding: '0.3rem' }}>Співробітник</th>
            <th style={{ padding: '0.3rem' }}>Категорія</th>
            <th style={{ padding: '0.3rem', textAlign: 'right' }}>Брутто</th>
            <th style={{ padding: '0.3rem', textAlign: 'right' }}>ПСП</th>
            <th style={{ padding: '0.3rem', textAlign: 'right' }}>База ПДФО</th>
            <th style={{ padding: '0.3rem', textAlign: 'right' }}>ПДФО</th>
            <th style={{ padding: '0.3rem' }}>Поріг</th>
          </tr>
        </thead>
        <tbody>
          {applied.map((it, idx) => {
            const gross = Number(it.gross_amount || 0);
            const pspAmt = Number(it.psp_amount || 0);
            const pdfoBase = Math.max(0, gross - pspAmt);
            const label = it.psp_category
              ? PSP_CATEGORY_LABELS[it.psp_category as PspCategory] ?? it.psp_category
              : '—';
            return (
              <tr
                key={`${it.id ?? idx}-${it.employee_id}`}
                data-testid={`payroll-psp-breakdown-row-${idx}`}
                style={{ borderTop: '1px solid #bfdbfe' }}
              >
                <td style={{ padding: '0.3rem' }}>{nameOf(it.employee_id)}</td>
                <td style={{ padding: '0.3rem' }}>{label}</td>
                <td style={{ padding: '0.3rem', textAlign: 'right' }}>{fmt(gross)}</td>
                <td style={{ padding: '0.3rem', textAlign: 'right', color: '#1e3a8a' }}>
                  −{fmt(pspAmt)}
                </td>
                <td style={{ padding: '0.3rem', textAlign: 'right' }}>{fmt(pdfoBase)}</td>
                <td style={{ padding: '0.3rem', textAlign: 'right', fontWeight: 600 }}>
                  {fmt(Number(it.pdfo_amount || 0))}
                </td>
                <td style={{ padding: '0.3rem', color: '#475569' }}>
                  ≤ {fmt(Number(it.psp_threshold || 0))}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p style={{ marginTop: '0.5rem', color: '#475569', fontSize: '0.78rem' }}>
        Формула: <strong>База ПДФО = Брутто − ПСП</strong>; ПСП застосовується
        якщо Брутто ≤ Поріг. ВЗ і ЄСВ нараховуються від Брутто (без зменшення на ПСП).
      </p>
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
